import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';
import { useLang } from '../i18n';
import { slugCliente } from '../data/casosDetalle';

const FALLBACK = [
    {
        id: '1',
        cliente: 'El Gato con Copas',
        sector: 'Hostelería',
        servicio: 'Branding + Digitalización + Growth',
        reto: 'Un negocio de hostelería con carácter propio que necesitaba una marca fuerte y procesos digitales para dar el salto.',
        resultado: '+180% de incremento en la facturación global del negocio.',
        metricas: [{ etiqueta: 'Facturación global', valor: '+180%' }],
    },
    {
        id: '2',
        cliente: 'La Patrona',
        sector: 'Eventos',
        servicio: 'Branding + Digitalización + SEO y SEM',
        reto: 'Una empresa de eventos que necesitaba una marca sólida, visibilidad online y procesos digitales para crecer.',
        resultado: '+75% de incremento en la facturación del último año.',
        metricas: [{ etiqueta: 'Facturación último año', valor: '+75%' }],
    },
    {
        id: '3',
        cliente: 'Burek',
        sector: 'Botánica',
        servicio: 'Branding + Digitalización',
        reto: 'Un negocio de botánica que quería modernizarse y pasar a un modelo digital automatizado.',
        resultado: 'Conversión a un modelo de negocio con automatización digital completa.',
        metricas: [{ etiqueta: 'Modelo de negocio', valor: 'Automatizado' }],
    },
];

const CLIENT_MAP = { 'El Gato con Copas': 'gato', 'La Patrona': 'patrona', 'Burek': 'burek' };

const ETIQUETA_MAP = {
    'Facturación global': 'casos_gato_metrica',
    'Facturación último año': 'casos_patrona_metrica',
    'Modelo de negocio': 'casos_burek_metrica',
    'Tráfico orgánico': 'casos_trafico',
    'Conversión': 'casos_metrica2',
    'CTR medio': 'casos_ctr',
    'Google regional': 'casos_google',
    'Leads/mes': 'casos_leads',
};

const VALOR_MAP = { 'Automatizado': 'casos_automatizado' };

export default function Cases() {
    const { t, lang } = useLang();
    const navigate = useNavigate();
    const [cases, setCases] = useState(FALLBACK);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (Array.isArray(data) && data.length) setCases(data);
            })
            .catch(() => {});
    }, []);

    const localize = (c) => {
        if (lang === 'es') return c;
        const k = CLIENT_MAP[c.cliente];
        if (!k) return c;
        return {
            ...c,
            reto: t(`casos_${k}_reto`),
            resultado: t(`casos_${k}_res`),
            metricas: (c.metricas || []).map((m) => ({
                etiqueta: ETIQUETA_MAP[m.etiqueta] ? t(ETIQUETA_MAP[m.etiqueta]) : m.etiqueta,
                valor: VALOR_MAP[m.valor] ? t(VALOR_MAP[m.valor]) : m.valor,
            })),
        };
    };

    return (
        <section id="casos" className="py-24 lg:py-32 bg-pastelcream/60 border-y border-ink/10" data-testid="casos-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <SectionHeading eyebrow={t('casos_eyebrow')} title={t('casos_title')} />
                    <Reveal delay={0.1}>
                        <p className="max-w-md text-ink/70 leading-relaxed lg:text-right">
                            {t('casos_intro')}
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
                    {cases.slice(0, 3).map((raw, i) => {
                        const c = localize(raw);
                        return (
                            <Reveal key={c.id || c.cliente} delay={i * 0.1} className="h-full">
                                <article
                                    id={`caso-${i + 1}`}
                                    className="group h-full flex flex-col rounded-[1.4rem] bg-white border border-ink/10 p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(27,42,74,0.4)]"
                                    data-testid={`caso-card-${i + 1}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-ink text-[10px] uppercase tracking-[0.2em] text-crimson border border-crimson/25 rounded-full px-3 py-1">
                                            {c.servicio}
                                        </span>
                                        <TrendingUp size={18} className="text-ink/25 group-hover:text-crimson transition-colors" />
                                    </div>
                                    <h3 className="font-display text-2xl font-semibold text-ink mt-6">{c.cliente}</h3>
                                    <p className="font-ink text-[11px] uppercase tracking-[0.18em] text-ink/45 mt-1.5">{c.sector}</p>
                                    <p className="mt-5 text-ink/75 leading-relaxed text-[15px]">
                                        <span className="font-bold text-ink">{t('casos_reto')}</span>
                                        {c.reto}
                                    </p>
                                    <p className="mt-3 text-ink/75 leading-relaxed text-[15px]">
                                        <span className="font-bold text-crimson">{t('casos_resultado')}</span>
                                        {c.resultado}
                                    </p>
                                    <div className="mt-auto pt-7 grid grid-cols-2 gap-3">
                                        {(c.metricas || []).map((m) => (
                                            <div key={m.etiqueta} className="rounded-xl bg-pastelblue/60 border border-ink/5 px-4 py-3" data-testid={`caso-metrica-${i + 1}`}>
                                                <div className="font-display text-xl font-semibold text-ink">{m.valor}</div>
                                                <div className="text-[10px] uppercase tracking-[0.14em] text-ink/55 font-semibold mt-0.5">{m.etiqueta}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {slugCliente(c.cliente) && (
                                        <button
                                            onClick={() => navigate(`/casos/${slugCliente(c.cliente)}`)}
                                            className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-crimson hover:gap-3.5 transition-all duration-300"
                                            data-testid={`caso-ver-${i + 1}`}
                                        >
                                            {t('caso_ver')} <ArrowRight size={13} />
                                        </button>
                                    )}
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
